import { useEffect, useId, useRef, useState, type FormEvent, type KeyboardEvent } from 'react'
import { createPortal } from 'react-dom'
import { Link } from 'react-router-dom'
import { FEEDBACK_KINDS, FEEDBACK_MESSAGE_MAX, type FeedbackKind } from '@chess-uno/shared-protocol'
import { feedbackErrorText, rememberAnswered, sendFeedback, shouldAskAfterGame } from '../api/feedback.js'
import { useI18n } from '../i18n/index.js'

/** Star of the score row (drawn here: Chrome imports this file for the footer link). */
function StarIcon({ size }: { size: number }) {
  return (
    <svg className="icon" width={size} height={size} viewBox="0 0 16 16" fill="currentColor" stroke="currentColor" strokeWidth="1" strokeLinejoin="round" aria-hidden="true">
      <path d="m8 1.8 1.9 4 4.3.5-3.2 3 .9 4.3L8 11.4 4.1 13.6 5 9.3 1.8 6.3l4.3-.5z" />
    </svg>
  )
}

const FOCUSABLE = 'button:not([disabled]), input:not([disabled]):not([tabindex="-1"]), textarea:not([disabled]), a[href]'

/**
 * Feedback form in a window. It only opens when asked (footer link, or "tell us
 * more" after a game score) and never takes the page over on its own.
 * What was typed is kept if the visitor clicks outside by mistake.
 */
export function FeedbackDialog({ onClose, gameId, initialKind = 'idea' }: { onClose: () => void; gameId?: string; initialKind?: FeedbackKind }) {
  const { locale, t, path } = useI18n()
  const [kind, setKind] = useState<FeedbackKind>(initialKind)
  const [message, setMessage] = useState('')
  const [contact, setContact] = useState('')
  const [trap, setTrap] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [done, setDone] = useState(false)
  const titleId = useId()
  const dialog = useRef<HTMLFormElement>(null)
  const field = useRef<HTMLTextAreaElement>(null)

  // Focus goes into the window, and back to what opened it when it closes.
  useEffect(() => {
    const opener = document.activeElement as HTMLElement | null
    field.current?.focus()
    return () => opener?.focus?.()
  }, [])

  // Escape works wherever the focus is (after sending, the button that had it is gone).
  // The page behind (a result window) must not also react to this key.
  const close = useRef(onClose)
  close.current = onClose
  useEffect(() => {
    const onEscape = (e: globalThis.KeyboardEvent) => {
      if (e.key !== 'Escape') return
      e.stopPropagation()
      close.current()
    }
    document.addEventListener('keydown', onEscape)
    return () => document.removeEventListener('keydown', onEscape)
  }, [])

  // Once sent, the focus moves to the way out instead of getting lost.
  const thanksButton = useRef<HTMLButtonElement>(null)
  useEffect(() => {
    if (done) thanksButton.current?.focus()
  }, [done])

  function onKeyDown(e: KeyboardEvent) {
    if (e.key !== 'Tab' || !dialog.current) return
    const items = [...dialog.current.querySelectorAll<HTMLElement>(FOCUSABLE)]
    const first = items[0]
    const last = items[items.length - 1]
    if (!first || !last) return
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault()
      last.focus()
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault()
      first.focus()
    }
  }

  async function submit(e: FormEvent) {
    e.preventDefault()
    if (busy || message.trim() === '') return
    setBusy(true)
    setError(null)
    const r = await sendFeedback({ kind, message, ...(contact.trim() ? { contact } : {}), ...(gameId ? { gameId } : {}), ...(trap ? { website: trap } : {}) }, locale)
    setBusy(false)
    if (r.ok) setDone(true)
    else setError(feedbackErrorText(r.key, locale))
  }

  const f = t.feedback
  return createPortal(
    <div
      className="overlay"
      role="presentation"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && message.trim() === '') onClose()
      }}
    >
      <form
        ref={dialog}
        className="overlay-card feedback-card"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onSubmit={(e) => void submit(e)}
        onKeyDown={onKeyDown}
        data-testid="feedback-dialog"
      >
        <button type="button" className="ghost icon-btn overlay-close" onClick={onClose} aria-label={f.close} title={f.close} data-testid="feedback-close">
          ✕
        </button>
        {done ? (
          <div className="feedback-thanks" data-testid="feedback-thanks">
            <h2 id={titleId}>{f.thanksTitle}</h2>
            <p>{f.thanksText}</p>
            <button ref={thanksButton} type="button" onClick={onClose}>
              {f.close}
            </button>
          </div>
        ) : (
          <>
            <h2 id={titleId}>{f.title}</h2>
            <p className="feedback-lead">{f.lead}</p>
            <div className="segmented" role="radiogroup" aria-label={f.kindLabel}>
              {FEEDBACK_KINDS.map((k) => (
                <button
                  key={k}
                  type="button"
                  role="radio"
                  aria-checked={kind === k}
                  className={kind === k ? 'active' : ''}
                  onClick={() => setKind(k)}
                  data-testid={`feedback-kind-${k}`}
                >
                  {f.kinds[k]}
                </button>
              ))}
            </div>
            <label className="text-field">
              <span>{f.messageLabel}</span>
              <textarea
                ref={field}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder={f.placeholders[kind]}
                maxLength={FEEDBACK_MESSAGE_MAX}
                rows={5}
                required
                data-testid="feedback-message"
              />
            </label>
            <label className="text-field">
              <span>{f.contactLabel}</span>
              <input
                value={contact}
                onChange={(e) => setContact(e.target.value)}
                placeholder={f.contactPlaceholder}
                maxLength={120}
                autoComplete="email"
                data-testid="feedback-contact"
              />
            </label>
            {/* Honeypot: invisible to people, tempting to bots. */}
            <input className="hp" tabIndex={-1} autoComplete="off" aria-hidden="true" name="website" value={trap} onChange={(e) => setTrap(e.target.value)} />
            <p className="fine">{f.fine((label) => <Link to={path('/confidentialite')}>{label}</Link>)}</p>
            {error && (
              <p className="error" role="alert" data-testid="feedback-error">
                {error}
              </p>
            )}
            <button type="submit" className="primary block" disabled={busy || message.trim() === ''} data-testid="feedback-send">
              {busy ? f.sending : f.send}
            </button>
          </>
        )}
      </form>
    </div>,
    document.body,
  )
}

/** Footer entry: the quiet, always available way to leave feedback. */
export function FeedbackLink() {
  const { t } = useI18n()
  const [open, setOpen] = useState(false)
  return (
    <>
      <button type="button" className="link footer-feedback" onClick={() => setOpen(true)} data-testid="feedback-open">
        {t.feedback.link}
      </button>
      {open && <FeedbackDialog onClose={() => setOpen(false)} />}
    </>
  )
}

/**
 * One line at the bottom of the result window: a tap on a star is the whole
 * answer and is sent at once; saying why is optional. Not asked after every
 * game (see `decideAsk`) and never in the way of the result or the actions.
 */
export function GameRating({ gameId }: { gameId: string }) {
  const { locale, t } = useI18n()
  const [ask] = useState(() => shouldAskAfterGame(gameId))
  const [rating, setRating] = useState<number | null>(null)
  const [hover, setHover] = useState<number | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [more, setMore] = useState(false)
  if (!ask) return null

  async function rate(value: number) {
    setError(null)
    setRating(value)
    const r = await sendFeedback({ rating: value, message: '', kind: 'other', gameId }, locale)
    if (r.ok) rememberAnswered()
    else {
      setRating(null)
      setError(feedbackErrorText(r.key, locale))
    }
  }

  const f = t.feedback
  const shown = hover ?? rating ?? 0
  return (
    <div className="game-rating" data-testid="game-rating">
      {rating === null ? (
        <>
          <span className="game-rating-label" id={`rating-${gameId}`}>
            {f.rateGame}
          </span>
          <div className="stars" role="radiogroup" aria-labelledby={`rating-${gameId}`} onMouseLeave={() => setHover(null)}>
            {[1, 2, 3, 4, 5].map((value) => (
              <button
                key={value}
                type="button"
                role="radio"
                aria-checked={false}
                aria-label={f.starLabel(value)}
                title={f.starLabel(value)}
                className={`star${value <= shown ? ' on' : ''}`}
                onMouseEnter={() => setHover(value)}
                onFocus={() => setHover(value)}
                onBlur={() => setHover(null)}
                onClick={() => void rate(value)}
                data-testid={`rate-${value}`}
              >
                <StarIcon size={22} />
              </button>
            ))}
          </div>
          {error && <span className="error small">{error}</span>}
        </>
      ) : (
        <>
          <span className="game-rating-label" data-testid="rating-thanks">
            {f.rated}
          </span>
          <button type="button" className="link" onClick={() => setMore(true)} data-testid="rating-more">
            {f.tellMore}
          </button>
          {more && <FeedbackDialog gameId={gameId} initialKind="other" onClose={() => setMore(false)} />}
        </>
      )}
    </div>
  )
}
